import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Alert,
  Button,
  Modal,
  Empty,
  Spin,
  message,
} from "antd";
import { InfoCircleOutlined, ExclamationCircleOutlined, DownloadOutlined } from "@ant-design/icons";
import RoleBasedApplications from "./RoleBasedApplications";
import { downloadCSV } from "../user/csvDownloader";
import useLogout from "../useLogout";
import "./RoleBasedAddOTP.css";

const COUNTRY_PREFIXES = {
  "233": "Ghana",
  "223": "Mali",
  "224": "Guinea",
};

const COUNTRY_ORDER = ["Ghana", "Mali", "Guinea", "Other"];

const groupNumbersByCountry = (numbers) => {
  const groups = { Ghana: [], Mali: [], Guinea: [], Other: [] };
  numbers.forEach((num) => {
    const str = String(num);
    const prefix = str.slice(0, 3);
    const country = COUNTRY_PREFIXES[prefix] || "Other";
    groups[country].push(num);
  });
  return groups;
};

const NUMBERS_PER_COLUMN = 50;

const NumbersByCountry = ({ groups }) => {
  const handleCopyCountry = (country, nums) => {
    const text = nums.join("\n");
    navigator.clipboard.writeText(text).then(
      () => message.success(`${country}: ${nums.length} numbers copied to clipboard`),
      () => message.error("Failed to copy")
    );
  };

  return (
    <>
      {COUNTRY_ORDER.map((country) => {
        const nums = groups[country] || [];
        if (nums.length === 0) return null;

        const columns = [];
        for (let i = 0; i < nums.length; i += NUMBERS_PER_COLUMN) {
          columns.push(nums.slice(i, i + NUMBERS_PER_COLUMN));
        }

        return (
          <div key={country} className="rbotp-country-section">
            <div className="rbotp-country-header">
              <span className="rbotp-country-name">{country}</span>
              <span className="rbotp-country-badge">{nums.length}</span>
              <Button
                size="small"
                onClick={() => handleCopyCountry(country, nums)}
                className="rbotp-copy-btn"
              >
                Copy All
              </Button>
            </div>
            <div className="rbotp-numbers-columns">
              {columns.map((column, colIndex) => (
                <div key={`${country}-col-${colIndex}`} className="rbotp-numbers-column">
                  {column.map((number, index) => (
                    <div key={`${country}-${colIndex}-${index}`} className="rbotp-number-item">
                      {number}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
};

const RoleBasedAddOTP = () => {
  const logout = useLogout();
  const apiUrl = process.env.REACT_APP_API_RoleBasedSims;
  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;

  const [numbers, setNumbers] = useState([]);
  const [fetchingMoreNumbers, setFetchingMoreNumbers] = useState(false);
  const [numbersAssigned, setNumbersAssigned] = useState(false);
  const [lockNumbersModalVisible, setLockNumbersModalVisible] = useState(false);
  const [noNumbersAvailable, setNoNumbersAvailable] = useState(false);
  const [numbersLocked, setNumbersLocked] = useState(false);
  const [loadingNumbers, setLoadingNumbers] = useState(false);
  const [showLockMoreAlert, setShowLockMoreAlert] = useState(true);
  const [selectedNumbers, setSelectedNumbers] = useState([]);

  const fetchAssignedNumbers = async () => {
    if (!user?.userId || !apiUrl) return;
    setLoadingNumbers(true);
    try {
      const response = await axios.get(`${apiUrl}/${user.userId}`);
      const body = response.data?.body ?? response.data;
      if (body?.inactive) {
        logout();
        return;
      }
      if (Array.isArray(body) && body.length > 0) {
        setNumbersAssigned(true);
        setNumbers(body.map((item) => item.msisdn ?? item));
        setNumbersLocked(true);
      } else {
        setNumbersAssigned(false);
      }
    } catch (error) {
      console.error("Error fetching assigned numbers:", error);
      message.error("Failed to load assigned numbers");
    } finally {
      setLoadingNumbers(false);
    }
  };

  const fetchAvailableNumbers = async () => {
    if (!user?.userId || !apiUrl) return;
    setLoadingNumbers(true);
    setNoNumbersAvailable(false);
    try {
      const response = await axios.get(`${apiUrl}?user_id=${user.userId}`);
      const body = response.data?.body ?? response.data;
      const sims = Array.isArray(body) ? body : [];
      if (sims.length === 0) {
        setNoNumbersAvailable(true);
      } else {
        setNoNumbersAvailable(false);
        setNumbers(sims.map((s) => s.msisdn ?? s));
        setSelectedNumbers(sims.map((s) => s.id ?? s));
        setNumbersAssigned(false);
      }
    } catch (error) {
      console.error("Error fetching available numbers:", error);
      message.error("Failed to load available numbers");
      setNoNumbersAvailable(true);
    } finally {
      setLoadingNumbers(false);
    }
  };

  useEffect(() => {
    if (user?.userId && apiUrl) {
      fetchAssignedNumbers();
    }
  }, [user?.userId, apiUrl]);

  useEffect(() => {
    if (numbersAssigned && numbers.length > 0 && showLockMoreAlert) {
      const totalThreshold = 500;
      if (numbers.length >= totalThreshold) {
        setShowLockMoreAlert(false);
      }
    }
  }, [numbersAssigned, numbers.length, showLockMoreAlert]);

  const handleLockNumbersClick = async () => {
    if (!user?.userId || !apiUrl || selectedNumbers.length === 0) return;
    try {
      await axios.post(`${apiUrl}?lock=true`, {
        user_id: user.userId,
        sims_id: selectedNumbers,
      });
      setNumbersLocked(true);
      setNumbersAssigned(true);
      setLockNumbersModalVisible(true);
    } catch (error) {
      console.error("Error locking numbers:", error);
      message.error("Failed to lock numbers");
    }
  };

  const handleLockMoreNumbers = async () => {
    if (!user?.userId || !apiUrl) return;
    setFetchingMoreNumbers(true);
    setLoadingNumbers(true);
    try {
      const response = await axios.get(`${apiUrl}/${user.userId}/check-limit`);
      const data = response.data;

      if (data.result === "true") {
        message.success(
          `${data.sims_limit ?? "More"} numbers locked successfully.`
        );
        setFetchingMoreNumbers(false);
        fetchAssignedNumbers();
      } else {
        setFetchingMoreNumbers(false);
        if (data.message === "Max limit reached") {
          setShowLockMoreAlert(false);
          message.info("Max limit reached");
        } else {
          message.error(
            `You cannot lock more numbers unless you generate ${data.otp_limit ?? data.required_otps ?? 0} OTPs for already locked numbers`
          );
        }
      }
    } catch (error) {
      console.error("Error locking more numbers:", error);
      message.error("An error occurred while locking more numbers.");
      setFetchingMoreNumbers(false);
    } finally {
      setLoadingNumbers(false);
    }
  };

  const handleDownloadClick = () => {
    downloadCSV(numbers);
  };

  const handleSelectApplicationsClick = () => {
    setLockNumbersModalVisible(true);
  };

  if (!user?.userId) {
    return <Alert message="Please log in to view this page." type="warning" />;
  }

  if (loadingNumbers && !numbersAssigned) {
    return (
      <div className="rbotp rbotp-loading">
        <Spin tip="Loading..." size="large" />
      </div>
    );
  }

  const groups = groupNumbersByCountry(numbers);

  return (
    <div className="rbotp">
      <div className="rbotp-page-header">
        <h1 className="rbotp-page-title">Run Test</h1>
        <p className="rbotp-page-subtitle">
          {numbersAssigned
            ? "Manage your assigned numbers and select applications for testing"
            : "Fetch and lock numbers to start generating OTPs"}
        </p>
      </div>

      {numbersAssigned ? (
        <>
          {showLockMoreAlert && (
            <div className="rbotp-lock-alert">
              <InfoCircleOutlined className="rbotp-lock-alert-icon" />
              <span className="rbotp-lock-alert-text">
                Are you sure that you have used all the assigned numbers? If so,
                click on the button to lock additional numbers.
              </span>
              <Button
                type="primary"
                size="middle"
                loading={fetchingMoreNumbers}
                onClick={handleLockMoreNumbers}
                className="rbotp-btn-primary"
              >
                Lock More Numbers
              </Button>
            </div>
          )}

          <div className="rbotp-card">
            <div className="rbotp-card-header">
              <h2 className="rbotp-card-title">
                Numbers Assigned To You for Today
              </h2>
              <div className="rbotp-actions">
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadClick}
                  className="rbotp-btn-secondary"
                >
                  Download CSV
                </Button>
                <Button
                  type="primary"
                  onClick={handleSelectApplicationsClick}
                  className="rbotp-btn-primary"
                >
                  Select Applications
                </Button>
              </div>
            </div>
            <div className="rbotp-card-body">
              <NumbersByCountry groups={groups} />
            </div>
          </div>
        </>
      ) : (
        <div className="rbotp-assignment-section">
          <div className="rbotp-assignment-card">
            <div className="rbotp-warning-alert">
            <ExclamationCircleOutlined className="rbotp-warning-alert-icon" />
            <span className="rbotp-warning-alert-text">
              If you don&apos;t click on Lock Numbers button, the numbers will
              not be locked for you and you will not be able to see the generated
              OTPs. Therefore make sure to click on Lock Numbers.
            </span>
            </div>

            <h2 className="rbotp-assignment-title">Numbers Assignment</h2>
            <p className="rbotp-assignment-desc">Fetch available numbers and lock them to reserve for your testing session.</p>

            <Button
            type="primary"
            onClick={fetchAvailableNumbers}
            loading={loadingNumbers}
            disabled={loadingNumbers}
            className="rbotp-btn-primary rbotp-fetch-btn"
          >
            {loadingNumbers ? "Fetching..." : "Fetch Available Numbers"}
            </Button>
          </div>

          {noNumbersAvailable && !loadingNumbers && (
            <div className="rbotp-empty">
              <Empty
                description="No numbers available for your role. Try again later."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          )}

          {numbers.length > 0 && !numbersAssigned && (
            <div className="rbotp-card">
              <div className="rbotp-card-header">
                <h2 className="rbotp-card-title">Numbers Available To Lock</h2>
                <Button
                  type="primary"
                  onClick={handleLockNumbersClick}
                  disabled={numbersLocked}
                  className="rbotp-btn-primary"
                >
                  Lock Numbers
                </Button>
              </div>
              <div className="rbotp-card-body">
                <NumbersByCountry groups={groups} />
              </div>
            </div>
          )}
        </div>
      )}

      <Modal
        title="Select Applications"
        open={lockNumbersModalVisible}
        width={560}
        onCancel={() => setLockNumbersModalVisible(false)}
        footer={null}
        className="rbotp-modal"
        centered
      >
        <RoleBasedApplications numbers={numbers} />
      </Modal>
    </div>
  );
};

export default RoleBasedAddOTP;
